import { Button } from "@lya-protect/lya-protect-form-library/dist/UI";
import { LinkButton } from "@src/components/linkButton/LinkButton";
import { NavButton } from "@src/components/navButton/NavButton";
import { OffreCardDownload } from "@src/components/offreCard/OffreCard";
import { _Activity } from "@src/Steps/simulate/activities/Activities";
import { SimulationPrestationsCollapse } from "@src/Steps/simulate/tarif/components/SimulationPrestationsCollapse";
import { formatCurrency } from "@src/utils";
import classNames from "classnames";
import { Fragment, useMemo } from "react";
import { ReactComponent as Check } from "@src/svg/check.svg";
import { StepName } from "../../step/Steps";
import { Tarif } from "./Tarif";
import "./tarifDesktop.scss";

function Included() {
  return (
    <p className="tarif-desktop-step__included">
      <Check className="tarif-desktop-step__included-svg" /> Inclus
    </p>
  );
}

function getTableCells(
  tarifs: Tarif[],
  selectTarif: (tarif: Tarif) => () => void,
  openGAssistanceModal: () => void
) {
  return [
    [
      <div />,
      <h2>{tarifs[0].name}</h2>,
      <h2>{tarifs[1].name}</h2>,
      <h2>{tarifs[2].name}</h2>,
    ],
    [
      <div>
        <h3>
          Garantie séquelles physiques et psychiques faisant suite à un accident
          de la vie courante
        </h3>
        <small>Indemnisation dès 1% d&apos;AIPP</small>
      </div>,
      <p>500 € par % d&apos;AIPP</p>,
      <p>1 000 € par % d&apos;AIPP</p>,
      <p>1 500 € par % d&apos;AIPP</p>,
    ],
    [
      <div>
        <h3>Garantie décès accidentel</h3>
        <small>
          Décès accidentel jusqu&apos;à l&apos;âge de 67 ans au plus
        </small>
      </div>,
      <p>60 000 €</p>,
      <p>120 000 €</p>,
      <p>180 000 €</p>,
    ],
    [
      <div className="tarif-desktop-step__table-label-gap">
        <div>
          <h3>
            Garanties assistance <br /> 24h/24 - 7j/7
          </h3>
        </div>
        <LinkButton onClick={openGAssistanceModal}>
          Détail des garanties
        </LinkButton>
      </div>,
      <Included />,
      <Included />,
      <Included />,
    ],
    [
      <h3>Montant de la cotisation</h3>,
      <div className="tarif-desktop-step__simulation">
        <p className="tarif-desktop-step__simulation-ttc">
          <b>{formatCurrency(tarifs[0].simulationCotisation)}</b>
          <span>TTC / mois</span>
        </p>
        <p>
          soit <b>{formatCurrency(tarifs[0].simulationCotisation * 12)}</b> TTC
          / an
        </p>
        <p>2 mois offerts pour toute souscription jusqu&apos;au 31/12/2026 *</p>
        {tarifs[0].simulationCotisation !== null && (
          <Button onClick={selectTarif(tarifs[0])}>Choisir</Button>
        )}
      </div>,
      <div className="tarif-desktop-step__simulation">
        <p className="tarif-desktop-step__simulation-ttc">
          <b>{formatCurrency(tarifs[1].simulationCotisation)}</b> TTC / mois
        </p>
        <p>
          soit <b>{formatCurrency(tarifs[1].simulationCotisation * 12)}</b> TTC
          / an
        </p>
        <p>2 mois offerts pour toute souscription jusqu&apos;au 31/12/2026 *</p>
        {tarifs[1].simulationCotisation !== null && (
          <Button onClick={selectTarif(tarifs[1])}>Choisir</Button>
        )}
      </div>,
      <div className="tarif-desktop-step__simulation">
        <p className="tarif-desktop-step__simulation-ttc">
          <b>{formatCurrency(tarifs[2].simulationCotisation)}</b> TTC / mois
        </p>
        <p>
          soit <b>{formatCurrency(tarifs[2].simulationCotisation * 12)}</b> TTC
          / an
        </p>
        <p>2 mois offerts pour toute souscription jusqu&apos;au 31/12/2026 *</p>
        {tarifs[2].simulationCotisation !== null && (
          <Button onClick={selectTarif(tarifs[2])}>Choisir</Button>
        )}
      </div>,
    ],
    [
      <div />,
      <OffreCardDownload />,
      <OffreCardDownload />,
      <OffreCardDownload />,
    ],
  ];
}

type TarifDesktopProps = {
  tarifs: Tarif[];
  openModalGassistance: () => void;
  selectTarif: (tarif: Tarif) => () => void;
  changeStep: (
    stepName: StepName,
    transitionDirection: "forward" | "backward"
  ) => void;
  visibleActivities: _Activity[];
};

export function TarifDesktop({
  openModalGassistance,
  tarifs,
  selectTarif,
  changeStep,
  visibleActivities,
}: TarifDesktopProps) {
  const tableContent = useMemo(
    () => getTableCells(tarifs, selectTarif, openModalGassistance),
    [tarifs]
  );

  return (
    <div className="tarif-desktop-step">
      <div className="stepper__centered-container">
        <div className="stepper__centered-content step__grid">
          <NavButton
            spacing="none"
            onClick={() => {
              changeStep("ACTIVITIES", "backward");
            }}
          >
            Précédent
          </NavButton>
        </div>
        <div className="stepper__centered-content stepper__centered-content--large step__grid">
          <div className="tarif-desktop-step__grid">
            <div />
            <div className="tarif-desktop-step__header">
              <h1>Choisissez votre niveau d&apos;indemnisation</h1>
            </div>
            {tableContent.slice(0, -1).map(([Label, ...Cols], index) => (
              <Fragment key={Label.key}>
                <div
                  className={classNames("tarif-desktop-step__table-label", {
                    "tarif-desktop-step__table-label-separator":
                      index !== 0 && index !== tableContent.length - 2,
                  })}
                >
                  {Label}
                </div>
                <div className="tarif-desktop-step__table-body">
                  {Cols.map((Col) => (
                    <div
                      key={Col.key}
                      className={classNames("tarif-desktop-step__table-col", {
                        "tarif-desktop-step__table-col-header": index === 0,
                        "tarif-desktop-step__table-col-body":
                          index !== 0 && index < tableContent.length - 2,
                        "tarif-desktop-step__table-col-footer":
                          index === tableContent.length - 2,
                      })}
                    >
                      {Col}
                    </div>
                  ))}
                </div>
              </Fragment>
            ))}

            <div className="tarif-desktop-step__table-label" />
            <div className="tarif-desktop-step__table-download">
              {tableContent[tableContent.length - 1].slice(1).map((Col) => Col)}
            </div>
            <div />
            <div className="tarif-desktop-step__info">
              <p>
                Le tableau des garanties et des tarifs ne comprend pas la liste
                des exclusions générales et des exclusions propres à chacune des
                garanties. Se référer au Règlement du contrat Paxivie.
              </p>
              <p>
                Les tarifs en vigueurs sont valables jusqu&apos;au{" "}
                <b>30/11/2026</b>, sous réserve de toute évolution.
              </p>
              <p>
                *Deux mois de cotisation offerts pour toute nouvelle
                souscription d&apos;un contrat Paxivie jusqu&apos;au 31 décembre
                2026. Le montant de ces cotisations ne sera pas prélevé sur
                votre compte bancaire au titre du paiement de votre contrat.
                Offre non cumulable avec toute autre offre en cours sur le
                contrat Paxivie.
              </p>
            </div>
          </div>
        </div>
        <div className="stepper__centered-content tarif-desktop-step__simuler-prestations">
          {visibleActivities.length > 0 && (
            <SimulationPrestationsCollapse
              visibleActivities={visibleActivities}
            />
          )}
        </div>
      </div>
    </div>
  );
}
